// Libraries
import React, { FC, StrictMode, useState } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { elementToSVG, inlineResources } from 'dom-to-svg';

// Components
import FileSelector from '@/components/FileSelector';
import EditForm from '@/components/EditForm/EditStylesForm';

// Styles
import '@/styles/index.css';

// Types
import {
  ChartData,
  ChartsHeaderType,
  ChartStyles,
  IconContainerType,
  MinMax,
} from '@/types';
import { Theme } from '@nivo/core';
import { SubmitHandler } from 'react-hook-form';

// Utils
import { onScatterFileLoad } from '@/utils/file';
import { WJP_DEFAULT_STYLES_SCATTER } from '@/utils/const';

// Hooks
import useWJPScatterCharts from '@/hooks/useWJPScatterCharts';

const IconContainer = styled.div<IconContainerType>`
  width: ${({ width }) => width || 40}px;
  height: ${({ height }) => (height ? `${height}px` : 'auto')};
`;

const ChartHeader = styled.h2<ChartsHeaderType>`
  font-size: ${({ fontSize }) => fontSize || 12}px;
`;

const App: FC = () => {
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [chartMeans, setChartMeans] = useState<(number | undefined)[]>([]);
  const [minMax, setMinMax] = useState<MinMax[]>([]);
  const [chartLoading, setChartLoading] = useState(false);

  const { charts, styles, setStyles } = useWJPScatterCharts({
    minMax,
    chartData,
    chartMeans,
  });

  const handleDownloadButtonClick = async () => {
    const group = document.getElementById('exportable-group');
    if (group) {
      const svgDocument = elementToSVG(group);

      // Inline external resources (fonts, images, etc) as data: URIs
      await inlineResources(svgDocument.documentElement);

      // Get SVG string
      const svgString = new XMLSerializer().serializeToString(svgDocument);

      const clean = DOMPurify.sanitize(svgString);

      const svgFile = new File([clean], `${styles.chartHeader}.svg`, {
        type: 'image/svg+xml',
      });

      const downloadUrl = URL.createObjectURL(svgFile);

      const link = document.createElement('a');
      link.download = `${styles.chartHeader}.svg`;
      link.href = downloadUrl;
      link.click();
    }
  };

  const onSubmit: SubmitHandler<ChartStyles> = ({ theme, ...data }) =>
    setStyles({
      ...data,
      theme: {
        background:
          theme.background || WJP_DEFAULT_STYLES_SCATTER.theme.background,
        annotations: {
          link: {
            ...(WJP_DEFAULT_STYLES_SCATTER.theme.annotations?.link || {}),
            ...(theme.annotations?.link || {}),
          },
          outline: {
            ...(WJP_DEFAULT_STYLES_SCATTER.theme.annotations?.outline || {}),
            ...(theme.annotations?.outline || {}),
          },
          symbol: {
            ...(WJP_DEFAULT_STYLES_SCATTER.theme.annotations?.symbol || {}),
            ...(theme.annotations?.symbol || {}),
          },
          text: {
            ...(WJP_DEFAULT_STYLES_SCATTER.theme.annotations?.text || {}),
            ...(theme.annotations?.text || {}),
          },
        },
        grid: {
          line: {
            ...(WJP_DEFAULT_STYLES_SCATTER.theme.grid?.line || {}),
            ...(theme.grid?.line || {}),
          },
        },
        axis: {
          ticks: {
            line: {
              ...(WJP_DEFAULT_STYLES_SCATTER.theme.axis?.ticks?.line || {}),
              ...(theme.axis?.ticks?.line || {}),
            },
            text: {
              ...(WJP_DEFAULT_STYLES_SCATTER.theme.axis?.ticks?.text || {}),
              ...(theme.axis?.ticks?.text || {}),
            },
          },
        },
      } as Theme,
    });

  return (
    <StrictMode>
      <main className="font-lato p-4 text-black-ish">
        <div className="flex flex-col xl:flex-row">
          <section className="w-full xl:w-5/12">
            <h1 className="text-4xl pb-4 font-bold">Graficador de datos</h1>
            <label htmlFor="csv-selector" className="text-lg">
              Para comenzar, por favor carga el archivo CSV que desees graficar:
            </label>
            <div className="pt-2">
              <div className="md:max-w-sm">
                <FileSelector
                  onFileLoad={async (file) =>
                    onScatterFileLoad({
                      csv: await file.text(),
                      setData: setChartData,
                      setDataMean: setChartMeans,
                      setDataMinMax: setMinMax,
                    })
                  }
                  id="csv-selector"
                  setFileLoading={setChartLoading}
                  multiple={false}
                  accept="text/csv,application/vnd.ms-excel,text/x-csv,application/csv,application/x-csv,text/comma-separated-values,text/x-comma-separated-values,text/tab-separated-values"
                  maxFiles={1}
                />
              </div>
              <details
                className="py-2 md:max-w-sm bg-white shadow-lg p-6 rounded-lg my-4 ring-1 ring-gray-200 cursor-pointer"
                open
              >
                <summary className="font-bold">Nota sobre el archivo:</summary>
                Salvo la agrupación de las gráficas, cada una de éstas tomará
                como encabezado el nombre de la columna del CSV que le
                corresponde.
              </details>
              <div
                className={`${chartLoading ? 'block md:max-w-sm' : 'hidden'}`}
              >
                <p>
                  <svg
                    role="status"
                    className="inline mr-2 w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-purple-700"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  Cargando...
                </p>
              </div>
              <aside
                className={`${
                  chartLoading || chartData.length === 0 ? 'hidden' : 'block'
                }`}
              >
                <h2 className="text-2xl pt-4 pb-2 font-bold">Estilos</h2>
                <EditForm onSubmit={onSubmit} chartData={chartData} />
              </aside>
              <button
                type="button"
                className={`my-2 px-4 py-2 rounded-lg w-40 shadow-xl active:shadow-none bg-green-100 border-4 text-green-900 border-green-900 cursor-pointer ${
                  chartLoading || chartData.length === 0 ? 'hidden' : 'block'
                }`}
                disabled={chartData.length === 0}
                onClick={handleDownloadButtonClick}
              >
                Descargar SVG
              </button>
            </div>
          </section>
          <section
            className={`w-full xl:w-7/12 flex flex-col items-start 2xl:items-center pt-2 xl:pt-0 ${
              chartLoading || chartData.length === 0 ? 'hidden' : 'block'
            }`}
          >
            <h2 className="text-2xl py-4 font-bold">Vista previa:</h2>
            <div className="overflow-auto">
              <div id="exportable-group">
                <div className="font-bold flex flex-row items-baseline">
                  {styles.chartIcon && (
                    <IconContainer>
                      <img
                        className="pr-2 w-full h-full"
                        src={styles.chartIcon}
                        alt="Ícono representativo de la gráfica"
                      />
                    </IconContainer>
                  )}
                  <ChartHeader
                    className="font-archer"
                    fontSize={styles.chartHeaderFontSize}
                  >
                    {styles.chartHeader}
                  </ChartHeader>
                </div>
                {charts}
              </div>
            </div>
          </section>
        </div>
      </main>
    </StrictMode>
  );
};

export default App;
